import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const FloatingChatInput = ({ 
  onSubmit, 
  onStop, 
  isLoading = false, 
  disabled = false 
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    
    onSubmit(inputMessage.trim());
    setInputMessage("");
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (disabled) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full bg-slate-50 dark:bg-gray-800 rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
    >
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Header */}
        <div className="flex items-center px-5 py-2.5 border-b border-border bg-brand-blue-light/30">
          <div className="flex items-center gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-pink animate-pulse"></div>
            <span className="text-xs font-medium text-brand-blue-dark font-sans">
              Ask a follow-up question
            </span>
          </div>
        </div>

        {/* Input Area */}
        <div className="px-5 py-3">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your question... (↵ to send)"
            className="w-full px-0 py-0 border-0 text-sm resize-none focus:outline-none focus:ring-0 bg-transparent text-foreground placeholder-brand-gray-medium/60 font-sans leading-relaxed"
            rows={2}
            disabled={isLoading}
          />
        </div>

        {/* Footer with Actions */}
        <div className="flex items-center justify-between px-5 py-3 bg-background border-t border-border">
          <div className="text-xs text-brand-gray-medium font-sans">
            {inputMessage.length > 0 ? `${inputMessage.length} characters` : 'Shift + ↵ for new line'}
          </div>
          <div className="flex items-center gap-2">
            {isLoading ? (
              <button
                type="button"
                onClick={onStop}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Stop
              </button>
            ) : (
              <button
                type="submit"
                disabled={!inputMessage.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-pink hover:bg-brand-pink/90 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium transition-all duration-200 hover:shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                Send
              </button>
            )}
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default FloatingChatInput;
